import { fetchWithXsrf } from "@siisel/frontend-shared";
import { CHARTS_DEV_SERVER_PORT, DEFAULT_API_ERROR_MESSAGE, LOCAL_API_BASE_URL, LOCAL_HOST_NAMES } from "../config/constants";
import type { ChainageCoordinate, LegacyLineDataset, ProfileChartResponse } from "../types";

type JQueryDeferred<T> = {
  done(callback: (result: T) => void): JQueryDeferred<T>;
  fail(callback: (...args: unknown[]) => void): JQueryDeferred<T>;
};

type AbpApi = {
  measurementChartClientApi?: {
    saveChartState(chartName: string, xMin?: number | null, xMax?: number | null, yMin?: number | null, yMax?: number | null): JQueryDeferred<void>;
    loadChartData(strPointIds: string, fromDate: string, toDate: string, strParameterTypeIds: string, isPreview?: boolean): JQueryDeferred<LegacyLineDataset[]>;
    getAdditionalLongitudinalProfileData(profileId: number, fromDate: string, toDate: string): JQueryDeferred<LegacyLineDataset[]>;
  };
  chainageService?: {
    getAll(excavationIds: number[], profileId?: number): JQueryDeferred<ChainageCoordinate[]>;
  };
  axisCoordinateService?: {
    getAll(axisId: number): JQueryDeferred<unknown[]>;
  };
  profileService?: {
    getProfileChart(profileId: number | null, nominalProfileId?: number): JQueryDeferred<ProfileChartResponse>;
  };
  nominalProfileService?: {
    getNominalProfileChart(profileId: number | null, nominalProfileId?: number): JQueryDeferred<ProfileChartResponse>;
  };
  cacheService?: {
    clearCache(cacheEntities: string, entityId: string): JQueryDeferred<void>;
  };
};

type AppWindow = Window & {
  __CHARTS_API_BASE_URL__?: string;
  abp?: {
    services?: {
      api?: AbpApi;
    };
  };
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function looksLikeHtmlError(value: string): boolean {
  const text = value.trim().toLowerCase();

  return (
    text.startsWith("<!doctype") ||
    text.startsWith("<?xml") ||
    text.startsWith("<html") ||
    text.includes("<title>error") ||
    text.includes("system.nullreferenceexception") ||
    text.includes("error processing request")
  );
}

function getJsonErrorMessage(value: unknown): string | null {
  if (!isRecord(value)) {
    return null;
  }

  const message = value.message;
  if (typeof message === "string" && message.trim() && !looksLikeHtmlError(message)) {
    return message.trim();
  }

  const error = value.error;
  if (isRecord(error)) {
    return getJsonErrorMessage(error);
  }

  return null;
}

function getRecordErrorMessage(value: Record<string, unknown>, fallback: string): string | null {
  const responseJsonMessage = getJsonErrorMessage(value.responseJSON);
  if (responseJsonMessage) {
    return responseJsonMessage;
  }

  const message = getJsonErrorMessage(value);
  if (message) {
    return message;
  }

  const responseText = value.responseText;
  if (typeof responseText === "string" && responseText.trim()) {
    return looksLikeHtmlError(responseText) ? fallback : responseText.trim();
  }

  const status = value.status;
  if (typeof status === "number" && status > 0) {
    return `HTTP ${status}`;
  }

  return null;
}

export function getSafeErrorMessage(error: unknown, fallback = DEFAULT_API_ERROR_MESSAGE): string {
  if (Array.isArray(error)) {
    for (const item of error) {
      const message = getSafeErrorMessage(item, "");
      if (message) {
        return message;
      }
    }

    return fallback;
  }

  if (error instanceof Error) {
    const message = error.message.trim();
    return message && !looksLikeHtmlError(message) ? message : fallback;
  }

  if (typeof error === "string") {
    const message = error.trim();
    return message && !looksLikeHtmlError(message) ? message : fallback;
  }

  if (isRecord(error)) {
    return getRecordErrorMessage(error, fallback) ?? fallback;
  }

  return fallback;
}

function deferredToPromise<T>(deferred: JQueryDeferred<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    deferred.done(resolve).fail((...args) => {
      reject(new Error(getSafeErrorMessage(args.length === 1 ? args[0] : args)));
    });
  });
}

function normalizeBaseUrl(value: string | undefined | null): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }

  return trimmed.replace(/\/+$/, "");
}

function getRuntimeDefaultApiBaseUrl(): string {
  const { hostname, port, origin } = window.location;
  const isViteDevHost =
    port === CHARTS_DEV_SERVER_PORT && LOCAL_HOST_NAMES.has(hostname);

  if (isViteDevHost) {
    return LOCAL_API_BASE_URL;
  }

  return origin;
}

function getApiBaseUrl(): string {
  const rootBase = document
    .querySelector<HTMLElement>("[data-charts-app-root='true']")
    ?.getAttribute("data-api-base-url");

  const envBase = import.meta.env.VITE_CHARTS_API_BASE_URL as string | undefined;
  const windowBase = (window as AppWindow).__CHARTS_API_BASE_URL__;

  return (
    normalizeBaseUrl(rootBase) ??
    normalizeBaseUrl(windowBase) ??
    normalizeBaseUrl(envBase) ??
    getRuntimeDefaultApiBaseUrl()
  );
}

function buildApiUrl(path: string): string {
  return `${getApiBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;
}

async function postAbp<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetchWithXsrf(buildApiUrl(path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    credentials: "include",
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} while calling ${path}`);
  }

  const json = (await response.json()) as { result?: T; Result?: T } | T;
  if (typeof json === "object" && json !== null) {
    if ("result" in json) {
      return (json as { result: T }).result ?? ({} as T);
    }
    if ("Result" in json) {
      return (json as { Result: T }).Result ?? ({} as T);
    }
  }

  return json as T;
}

function getAbpApi(): AbpApi | null {
  return (window as AppWindow).abp?.services?.api ?? null;
}

export async function saveChartState(
  chartName: string,
  xMin?: number | null,
  xMax?: number | null,
  yMin?: number | null,
  yMax?: number | null
): Promise<void> {
  const service = getAbpApi()?.measurementChartClientApi;

  if (service?.saveChartState) {
    await deferredToPromise(service.saveChartState(chartName, xMin, xMax, yMin, yMax));
    return;
  }

  await postAbp<void>("/api/services/app/MeasurementChartClientApi/SaveChartState", {
    chartName,
    xMin,
    xMax,
    yMin,
    yMax
  });
}

export async function loadChartData(
  pointIdsCsv: string,
  fromDate: string,
  toDate: string,
  parameterTypeIdsCsv: string,
  isPreview: boolean
): Promise<LegacyLineDataset[]> {
  const service = getAbpApi()?.measurementChartClientApi;

  if (service?.loadChartData) {
    return deferredToPromise(
      service.loadChartData(pointIdsCsv, fromDate, toDate, parameterTypeIdsCsv, isPreview)
    );
  }

  return postAbp<LegacyLineDataset[]>("/api/services/app/MeasurementChartClientApi/LoadChartData", {
    strPointIds: pointIdsCsv,
    fromDate,
    toDate,
    strParameterTypeIds: parameterTypeIdsCsv,
    isPreview
  });
}

export async function getAdditionalLongitudinalProfileData(
  profileId: number,
  fromDate: string,
  toDate: string
): Promise<LegacyLineDataset[]> {
  const service = getAbpApi()?.measurementChartClientApi;

  if (service?.getAdditionalLongitudinalProfileData) {
    return deferredToPromise(service.getAdditionalLongitudinalProfileData(profileId, fromDate, toDate));
  }

  return postAbp<LegacyLineDataset[]>(
    "/api/services/app/MeasurementChartClientApi/GetAdditionalLongitudinalProfileData",
    {
      profileId,
      fromDate,
      toDate
    }
  );
}

export async function getChainageData(
  excavationIds: number[],
  profileId?: number
): Promise<ChainageCoordinate[]> {
  const service = getAbpApi()?.chainageService;

  if (service?.getAll) {
    return deferredToPromise(service.getAll(excavationIds, profileId));
  }

  return postAbp<ChainageCoordinate[]>("/api/services/app/ChainageService/GetAll", {
    excavationIds,
    profileId
  });
}

export async function getAxisCoordinates(axisId: number): Promise<unknown[]> {
  const service = getAbpApi()?.axisCoordinateService;

  if (service?.getAll) {
    return deferredToPromise(service.getAll(axisId));
  }

  return postAbp<unknown[]>("/api/services/app/AxisCoordinateService/GetAll", {
    axisId
  });
}

export async function getProfileChart(
  profileId: number | undefined,
  nominalProfileId: number | undefined,
  isNominalProfile: boolean
): Promise<ProfileChartResponse> {
  const api = getAbpApi();
  let response: ProfileChartResponse;

  if (isNominalProfile && api?.nominalProfileService?.getNominalProfileChart) {
    response = await deferredToPromise(api.nominalProfileService.getNominalProfileChart(profileId ?? null, nominalProfileId));
  } else if (!isNominalProfile && api?.profileService?.getProfileChart) {
    response = await deferredToPromise(api.profileService.getProfileChart(profileId ?? null, nominalProfileId));
  } else if (isNominalProfile) {
    response = await postAbp<ProfileChartResponse>("/api/services/app/NominalProfileService/GetNominalProfileChart", {
      profileId: profileId ?? null,
      nominalProfileId
    });
  } else {
    response = await postAbp<ProfileChartResponse>("/api/services/app/ProfileService/GetProfileChart", {
      profileId: profileId ?? null,
      nominalProfileId
    });
  }

  const wrapped = response as ProfileChartResponse & {
    result?: ProfileChartResponse;
    Result?: ProfileChartResponse;
  };
  return wrapped.result ?? wrapped.Result ?? response;
}

export async function clearCache(cacheEntities: string, entityId: string): Promise<void> {
  const response = await fetchWithXsrf(buildApiUrl(`/api/publicApi/ClearCache?entityId=${encodeURIComponent(entityId)}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    credentials: "include",
    body: JSON.stringify(cacheEntities)
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} while calling /api/publicApi/ClearCache`);
  }
}
